import React, { useEffect } from "react";
import { useCurrency } from "@/hooks/useCurrency";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import { format } from "date-fns";
import { ChildSelector } from "./ChildSelector";
import { ContractWizardStepSchedule } from "./ContractWizardStepSchedule";
import { ContractWizardStepDiscounts } from "./ContractWizardStepDiscounts";
import { EnhancedContractSummary } from "./EnhancedContractSummary";
import { useMultiGroupCostCalculation } from "@/hooks/useMultiGroupCostCalculation";
import { usesSubsidisedRates } from "@/utils/contracts/subsidisedRates";
import type {
  ContractFamilyGroupDraft,
  EnhancedMultiGroupCostResult,
} from "@/types/billing";
import { ContractWizardStepPayers } from "./ContractWizardStepPayers";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { PayerRequest, Service } from "@/types/billing";
import type { Contact } from "@/types/contact";
import { useChild } from "@/hooks/useChildren";
import { BillingDurationCard } from "./BillingDurationCard";
import { useQueryClient } from "@tanstack/react-query";
import { TariffsService } from "@/api/services/TariffsService";
import { useAuth } from "@clerk/clerk-react";
import { OrganisationSettingsService } from "@/api";
import { useCustomEnums } from "@/hooks/settings";
import { AutoCalculateDatesButton } from "@/components/contracts/AutoCalculateDatesButton";
import { rappenToCHF, chfToRappen } from "@/lib/money";
import { useSubsidyOffices } from "@/hooks/useSubsidyOffices";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface MultiGroupServicePattern {
  [groupType: string]: Record<
    string,
    import("@/types/billing").ServiceDetail[]
  >;
}

interface TariffVersions {
  [groupType: string]: string;
}

type FamilyGroup = ContractFamilyGroupDraft;

interface WizardData {
  childId?: string;
  childcareId?: string;
  startDate?: string;
  endDate?: string;
  subsidised?: boolean;
  subsidyAmount?: number;
  /** On-top Subventionszuschuss in Rappen (billed to the office; never reduces the parent). */
  subsidySupplementAmount?: number;
  subsidyOfficeId?: string;
  beitragsfaktor?: number;
  beitragsfaktorValidUntil?: string;
  siblingDiscountManual?: boolean;
  subsidyDecisionPending?: boolean;
  servicePattern?: MultiGroupServicePattern;
  payers?: PayerRequest[];
  notes?: string;
  tariffVersions?: TariffVersions;
  billingPeriodOption?: "same" | "service_based" | "custom";
  billingStartDate?: string;
  billingEndDate?: string;
  discounts?: {
    weekday?: { [day: number]: number };
    custom?: Array<{
      id?: string;
      name: string;
      type: "percentage" | "amount";
      value: number;
    }>;
  };
  familyGroup?: FamilyGroup;
}

interface ContractWizardStepsProps {
  currentStep: string;
  wizardData: WizardData;
  onUpdateData: (data: Partial<WizardData>) => void;
  stepIndex?: number;
  contacts?: Contact[];
  services?: Service[];
}

/**
 * Compute the wizard-data patch for toggling the "Subsidised" flag. Turning it
 * OFF clears the BF-only fields so a stale Beitragsfaktor never reaches the
 * backend.
 */
function toggleSubsidised(checked: boolean): Partial<WizardData> {
  if (checked) {
    return { subsidised: true };
  }
  return {
    subsidised: false,
    beitragsfaktor: undefined,
    beitragsfaktorValidUntil: undefined,
  };
}

/** Subsidy office row shape this module needs to resolve a calc method. */
interface OfficeMethodSource {
  id?: string;
  calculation_method?: string;
}

/**
 * Resolve a subsidy office's calculation method. Defaults to `zurich_bf` when
 * the office is unknown or its method is blank — matching the backend's
 * read-time default and the `selectedOfficeMethod` memo below.
 */
function resolveOfficeMethod(
  officeId: string | undefined,
  offices: OfficeMethodSource[]
): "zurich_bf" | "flat" {
  const office = offices.find((o) => o.id === officeId);
  return office?.calculation_method === "flat" ? "flat" : "zurich_bf";
}

/**
 * Compute the wizard-data patch for picking a subsidy office. Because the
 * fields shown depend on the office's calculation_method, switching to an
 * office with a DIFFERENT method must clear the now-hidden field so a stale
 * value never reaches the backend (which 400s on a forbidden field):
 *   - zurich_bf → clears the flat `subsidyAmount`
 *   - flat      → clears `beitragsfaktor` + `beitragsfaktorValidUntil`
 * `subsidySupplementAmount` is valid for both methods and is never cleared.
 */
export function selectSubsidyOffice(
  officeId: string,
  offices: OfficeMethodSource[]
): Partial<WizardData> {
  const method = resolveOfficeMethod(officeId, offices);
  if (method === "flat") {
    return {
      subsidyOfficeId: officeId,
      beitragsfaktor: undefined,
      beitragsfaktorValidUntil: undefined,
    };
  }
  return {
    subsidyOfficeId: officeId,
    subsidyAmount: undefined,
  };
}

export function ContractWizardSteps({
  currentStep,
  wizardData,
  onUpdateData,
  stepIndex: _stepIndex = 0,
  contacts,
  services,
}: ContractWizardStepsProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  const { data: customEnums = [] } = useCustomEnums({ entityType: "groups" });

  const groups = React.useMemo(
    () => customEnums.filter((e) => e.enum_field === "group_type"),
    [customEnums]
  );

  const { data: subsidyOfficesData } = useSubsidyOffices();
  const subsidyOffices = React.useMemo(
    () => subsidyOfficesData?.items ?? [],
    [subsidyOfficesData]
  );

  // The selected office's calculation_method drives which subsidy fields show:
  //   zurich_bf → Beitragsfaktor + BF-valid-until
  //   flat      → flat subsidy_amount
  // Defaults to zurich_bf when the office row has a blank/unknown method.
  const selectedOfficeMethod = React.useMemo<"zurich_bf" | "flat">(
    () => resolveOfficeMethod(wizardData.subsidyOfficeId, subsidyOffices),
    [subsidyOffices, wizardData.subsidyOfficeId]
  );

  const hasOfficeSelected =
    typeof wizardData.subsidyOfficeId === "string" &&
    wizardData.subsidyOfficeId.length > 0;

  // Prefetch step-3 data while on step 2 (details) to reduce flicker
  useEffect(() => {
    if (currentStep !== "details") return;

    queryClient.prefetchQuery({
      queryKey: ["tariff-versions", "active"],
      queryFn: async () => {
        const resp = await TariffsService.getApiV1TariffVersions(
          undefined,    // groupType
          "ACTIVE",     // status
          undefined,    // isDefault
          false,        // includeRates
          undefined,    // search
          'created_at', // sortBy
          'desc',       // sortOrder
          100,          // limit
          0             // offset
        );
        return resp?.data || [];
      },
      staleTime: 5 * 60 * 1000,
    });

    if (orgId) {
      queryClient.prefetchQuery({
        queryKey: ["effective-enums", orgId, "groups", "type", i18n.locale],
        queryFn: async () => {
          const response =
            await OrganisationSettingsService.getApiV1SettingsEffective();
          interface ApiEnumItem {
            enum_value?: string;
            label_default?: string;
            is_system?: boolean;
            is_active?: boolean;
            sort_order?: number;
          }

          interface ProcessedEnumItem {
            value: string;
            label: string;
            isSystem: boolean;
            isActive: boolean;
            sortOrder?: number;
          }

          const apiEnums = response?.enums?.["groups"]?.["type"] || [];
          const active = apiEnums
            .filter((e: ApiEnumItem) => e?.is_active !== false)
            .map(
              (e: ApiEnumItem): ProcessedEnumItem => ({
                value: e.enum_value || "",
                label: e.label_default || e.enum_value || "",
                isSystem: !!e.is_system,
                isActive: e.is_active !== false,
                sortOrder: e.sort_order,
              })
            )
            .filter((e: ProcessedEnumItem) => e.isActive)
            .sort(
              (a: ProcessedEnumItem, b: ProcessedEnumItem) =>
                (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
            );
          return active;
        },
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [currentStep, queryClient, orgId, i18n.locale]);
  // Fetch selected child directly by ID to reliably get birthdate
  // (avoids pagination limit issues with the children list query)
  const { data: selectedChild } = useChild(wizardData.childId || '', { enabled: !!wizardData.childId });

  const rawSelectedChildBirthdate = React.useMemo(() => {
    if (!selectedChild) return undefined;
    const birthdate =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (selectedChild as any)?.birthdate || (selectedChild as any)?.birth_date;
    return typeof birthdate === "string" ? birthdate : undefined;
  }, [selectedChild]);

  const selectedChildBirthdate = React.useMemo(() => {
    if (!rawSelectedChildBirthdate) return undefined;
    const match = rawSelectedChildBirthdate.match(/^(\d{4}-\d{2}-\d{2})/);
    if (!match) {
      return undefined;
    }
    return match[1];
  }, [rawSelectedChildBirthdate]);

  const autoCalcMissingRequirements = React.useMemo(() => {
    const missing: string[] = [];

    if (!rawSelectedChildBirthdate) {
      missing.push(i18n._(msg`the child's birthdate`));
    } else if (!selectedChildBirthdate) {
      missing.push(i18n._(msg`a valid child birthdate`));
    }

    if (!wizardData.childcareId) {
      missing.push(i18n._(msg`a childcare location`));
    }

    return missing;
  }, [
    rawSelectedChildBirthdate,
    selectedChildBirthdate,
    wizardData.childcareId,
    i18n,
  ]);

  const autoCalcDisabled = autoCalcMissingRequirements.length > 0;

  const autoCalcDisabledMessage = React.useMemo(() => {
    if (!autoCalcDisabled) {
      return null;
    }

    const formatter = new Intl.ListFormat(i18n.locale || "en", {
      style: "long",
      type: "conjunction",
    });

    const formattedList = formatter.format(autoCalcMissingRequirements);
    return `${i18n._(msg`Auto calculation requires`)} ${formattedList}.`;
  }, [autoCalcDisabled, autoCalcMissingRequirements, i18n]);

  // Removed default family group auto-initialization to respect explicit user choice

  // Stabilize optional array/object props to avoid identity churn
  const EMPTY_SERVICES = React.useMemo<Service[]>(() => [], []);
  const EMPTY_OBJ = React.useMemo(() => ({}), []);

  const stableServices = services ?? EMPTY_SERVICES;

  // Check if any services are configured
  const hasServices = React.useMemo(() => {
    if (!wizardData.servicePattern) return false;
    return Object.values(wizardData.servicePattern).some((pattern) =>
      Object.values(pattern).some((services) => services && services.length > 0)
    );
  }, [wizardData.servicePattern]);

  // Render step content with focus management
  // Calculate enhanced summary for review - always call hook at top level
  const reviewCalculationDate =
    wizardData.billingStartDate || wizardData.startDate || undefined;

  const reviewCostResult = useMultiGroupCostCalculation(
    wizardData.servicePattern ??
      (EMPTY_OBJ as unknown as MultiGroupServicePattern),
    wizardData.tariffVersions ?? (EMPTY_OBJ as unknown as TariffVersions),
    wizardData.childId || "",
    stableServices,
    wizardData.discounts,
    {
      useEnhancedAPI: true,
      includeDiscounts: true,
      isSubsidised: usesSubsidisedRates(
        wizardData.subsidised,
        wizardData.subsidyOfficeId,
        selectedOfficeMethod,
      ),
      calculationDate: reviewCalculationDate,
      contractStartDate: wizardData.startDate,
      contractEndDate: wizardData.endDate,
      fullContractView: true,
    }
  ) as EnhancedMultiGroupCostResult;

  // Only use result if on review step and have necessary data
  const validReviewResult = React.useMemo(() => {
    if (
      currentStep !== "review" ||
      !wizardData.servicePattern ||
      !wizardData.tariffVersions ||
      !wizardData.childId
    ) {
      return null;
    }
    return reviewCostResult;
  }, [
    currentStep,
    wizardData.servicePattern,
    wizardData.tariffVersions,
    wizardData.childId,
    reviewCostResult,
  ]);

  const renderStepContent = () => {
    switch (currentStep) {
      case "child":
        return (
          <div role="tabpanel" aria-labelledby="step-child" className="h-full">
            <ChildSelector
              selectedChildId={wizardData.childId}
              selectedChildcareId={wizardData.childcareId}
              onSelectChild={(childId) => onUpdateData({ childId })}
              onSelectChildcare={(childcareId) => onUpdateData({ childcareId })}
            />
          </div>
        );

      case "details":
        return (
          <div
            role="tabpanel"
            aria-labelledby="step-details"
            className="space-y-6"
          >
            {/* Prefetch runs via top-level useEffect when on details step */}
            <div>
              <h3 className="text-lg font-medium">
                {i18n._(msg`Contract Details`)}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {i18n._(
                  msg`Specify the contract period and additional details`
                )}
              </p>
            </div>

            <div className="space-y-6">
              {/* Contract Duration */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      <Trans id="contracts.wizard.fields.contractDuration">
                        Contract Duration
                      </Trans>
                    </CardTitle>
                    <AutoCalculateDatesButton
                      childBirthdate={selectedChildBirthdate}
                      childcareId={wizardData.childcareId}
                      disabled={autoCalcDisabled}
                      onDatesCalculated={(startDate, endDate) => {
                        const updates: Partial<WizardData> = {
                          startDate: startDate
                            ? format(startDate, "yyyy-MM-dd")
                            : undefined,
                          endDate: endDate
                            ? format(endDate, "yyyy-MM-dd")
                            : undefined,
                        };

                        // Update billing dates if option is "same"
                        if (
                          wizardData.billingPeriodOption === "same" ||
                          !wizardData.billingPeriodOption
                        ) {
                          updates.billingStartDate = updates.startDate;
                          updates.billingEndDate = updates.endDate;
                        }

                        onUpdateData(updates);
                      }}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {autoCalcDisabledMessage && (
                    <p className="text-sm text-warning-on-surface mb-3">
                      {autoCalcDisabledMessage}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-date">
                        <Trans id="contracts.wizard.fields.startDate">
                          Start Date
                        </Trans>
                      </Label>
                      <DatePicker
                        id="start-date"
                        value={
                          wizardData.startDate
                            ? new Date(wizardData.startDate)
                            : undefined
                        }
                        onChange={(date) => {
                          const formattedDate = date
                            ? format(date, "yyyy-MM-dd")
                            : undefined;
                          const updates: Partial<WizardData> = {
                            startDate: formattedDate,
                          };

                          // Update billing start date if option is "same"
                          if (wizardData.billingPeriodOption === "same") {
                            updates.billingStartDate = formattedDate;
                          }

                          onUpdateData(updates);
                        }}
                        placeholder={i18n._(msg`Select start date`)}
                        aria-label={i18n._(msg`Select start date`)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end-date">
                        <Trans id="contracts.wizard.fields.endDate">
                          End Date
                        </Trans>
                      </Label>
                      <DatePicker
                        id="end-date"
                        value={
                          wizardData.endDate
                            ? new Date(wizardData.endDate)
                            : undefined
                        }
                        onChange={(date) => {
                          const formattedDate = date
                            ? format(date, "yyyy-MM-dd")
                            : undefined;
                          const updates: Partial<WizardData> = {
                            endDate: formattedDate,
                          };

                          // Update billing end date if option is "same"
                          if (wizardData.billingPeriodOption === "same") {
                            updates.billingEndDate = formattedDate;
                          }

                          onUpdateData(updates);
                        }}
                        placeholder={i18n._(msg`Select end date`)}
                        aria-label={i18n._(msg`Select end date`)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Billing Period - Progressive Disclosure */}
              <BillingDurationCard
                startDate={wizardData.startDate || ""}
                endDate={wizardData.endDate}
                billingStartDate={wizardData.billingStartDate}
                billingEndDate={wizardData.billingEndDate}
                hasServices={hasServices}
                servicePattern={
                  wizardData.servicePattern as unknown as Record<
                    string,
                    Record<string, Array<{ serviceCode: string }>>
                  >
                }
                billingOption={wizardData.billingPeriodOption}
                onBillingOptionChange={(opt) =>
                  onUpdateData({ billingPeriodOption: opt })
                }
                onBillingDatesChange={(start, end) => {
                  onUpdateData({
                    billingStartDate: start,
                    billingEndDate: end,
                  });
                }}
              />

              {/* Subsidised Contract */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    <Trans id="contracts.wizard.fields.subsidised">
                      Subsidised Contract
                    </Trans>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Subsidised toggle. Turning it off clears the BF-only fields. */}
                  <div
                    className="flex items-center justify-between cursor-pointer hover:bg-accent/50 transition-colors rounded-md p-2"
                    onClick={() =>
                      onUpdateData(toggleSubsidised(!wizardData.subsidised))
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onUpdateData(
                          toggleSubsidised(!wizardData.subsidised),
                        );
                      }
                    }}
                    aria-pressed={wizardData.subsidised || false}
                  >
                    <p className="text-sm text-muted-foreground select-none">
                      <Trans id="contracts.wizard.fields.subsidisedDescription">
                        This contract receives government subsidies or financial
                        assistance
                      </Trans>
                    </p>
                    <Switch
                      id="subsidised-switch"
                      checked={wizardData.subsidised || false}
                      onCheckedChange={(checked) => {
                        onUpdateData(toggleSubsidised(checked));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={i18n._(msg`Toggle subsidised status`)}
                    />
                  </div>

                  {/* Office picker is always available so a non-subsidised
                      contract can still attach a Subventionszuschuss. */}
                  <div className="space-y-2">
                    <Label htmlFor="contract-subsidy-office">
                      <Trans id="contracts.wizard.fields.subsidyOffice">
                        Subsidy office
                      </Trans>
                    </Label>
                    <Select
                      value={wizardData.subsidyOfficeId ?? undefined}
                      onValueChange={(val) =>
                        onUpdateData(selectSubsidyOffice(val, subsidyOffices))
                      }
                    >
                      <SelectTrigger id="contract-subsidy-office">
                        <SelectValue
                          placeholder={i18n._(msg({
                            id: "contracts.wizard.fields.subsidyOffice.placeholder",
                            message: "Select a subsidy office",
                          }))}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {subsidyOffices
                          .filter((o) => !!o.id)
                          .map((o) => (
                            <SelectItem key={o.id} value={o.id as string}>
                              {o.name ?? o.id}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subsidy decision pending hold — only for a subsidised
                      contract (any method, even before an office is selected).
                      While on, the invoice is withheld until the office's
                      decision (Beitragsfaktor or flat amount) is entered. */}
                  {wizardData.subsidised && (
                    <div className="flex items-start gap-3">
                      <Switch
                        id="subsidy-decision-pending-switch"
                        checked={wizardData.subsidyDecisionPending || false}
                        onCheckedChange={(checked) =>
                          onUpdateData({ subsidyDecisionPending: checked })
                        }
                        aria-label={i18n._(msg`Toggle subsidy decision pending`)}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="subsidy-decision-pending-switch">
                          <Trans id="contracts.wizard.fields.subsidyDecisionPending">
                            Subsidy decision pending
                          </Trans>
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          <Trans id="contracts.wizard.fields.subsidyDecisionPending.help">
                            The subsidy office has not issued its decision yet.
                            The invoice is withheld until you enter the decision
                            and switch this off — the contract still counts toward
                            revenue forecasts.
                          </Trans>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Flat base subsidy — only for a subsidised contract whose
                      office uses the flat calculation method. */}
                  {wizardData.subsidised &&
                    selectedOfficeMethod === "flat" && (
                      <div className="space-y-2">
                        <Label htmlFor="subsidy-amount">
                          {i18n._({
                            id: "contracts.wizard.fields.subsidyAmount",
                            message: "Flat subsidy amount ({currency} / month)",
                            values: { currency },
                          })}
                        </Label>
                        <Input
                          id="subsidy-amount"
                          type="number"
                          step="0.05"
                          min="0"
                          max="10000"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={
                            wizardData.subsidyAmount !== undefined
                              ? rappenToCHF(wizardData.subsidyAmount)
                              : ""
                          }
                          onChange={(e) => {
                            if (e.target.value === "") {
                              onUpdateData({ subsidyAmount: undefined });
                            } else {
                              const parsedCHF = parseFloat(e.target.value);
                              if (!isNaN(parsedCHF) && parsedCHF >= 0) {
                                const clampedRappen = chfToRappen(parsedCHF);
                                onUpdateData({ subsidyAmount: clampedRappen });
                              }
                            }
                          }}
                          aria-label={i18n._(msg`Subsidy amount`)}
                        />
                        <p className="text-xs text-muted-foreground">
                          <Trans id="contracts.wizard.fields.subsidyAmount.flatHelp">
                            Flat monthly base subsidy paid by the office; reduces the parent invoice.
                          </Trans>
                        </p>
                      </div>
                    )}

                  {/* Beitragsfaktor + BF valid until — only for a subsidised
                      contract whose office uses the zurich_bf method. */}
                  {wizardData.subsidised &&
                    selectedOfficeMethod === "zurich_bf" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="beitragsfaktor">
                            <Trans id="contracts.wizard.fields.beitragsfaktor">
                              Beitragsfaktor (%)
                            </Trans>
                          </Label>
                          <Input
                            id="beitragsfaktor"
                            type="number"
                            step="0.01"
                            min={0}
                            max={100}
                            inputMode="decimal"
                            value={
                              wizardData.beitragsfaktor !== undefined && wizardData.beitragsfaktor !== null
                                ? Math.round(wizardData.beitragsfaktor * 10000) / 100
                                : ""
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                onUpdateData({ beitragsfaktor: undefined });
                                return;
                              }
                              const pct = parseFloat(raw);
                              if (!Number.isFinite(pct)) {
                                onUpdateData({ beitragsfaktor: undefined });
                                return;
                              }
                              onUpdateData({
                                beitragsfaktor: Math.round((pct / 100) * 10000) / 10000,
                              });
                            }}
                            aria-label={i18n._(msg({
                              id: "contracts.wizard.fields.beitragsfaktor.aria",
                              message: "Beitragsfaktor percentage",
                            }))}
                          />
                          <p className="text-xs text-muted-foreground">
                            <Trans id="contracts.wizard.fields.beitragsfaktor.help">
                              Between 0 and 100% (e.g. 92.10).
                            </Trans>
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="bf-valid-until">
                            <Trans id="contracts.wizard.fields.beitragsfaktorValidUntil">
                              BF valid until
                            </Trans>
                          </Label>
                          <DatePicker
                            value={
                              wizardData.beitragsfaktorValidUntil
                                ? new Date(wizardData.beitragsfaktorValidUntil)
                                : undefined
                            }
                            onChange={(d) =>
                              onUpdateData({
                                beitragsfaktorValidUntil: d ? format(d, "yyyy-MM-dd") : undefined,
                              })
                            }
                            aria-label={i18n._(msg({
                              id: "contracts.wizard.fields.beitragsfaktorValidUntil.aria",
                              message: "Beitragsfaktor valid until date",
                            }))}
                          />
                        </div>
                      </>
                    )}

                  {/* On-top Subventionszuschuss — shown whenever a subsidy
                      office is selected (including on non-subsidised
                      contracts). Billed to the office; never reduces the
                      parent invoice. */}
                  {hasOfficeSelected && (
                    <div className="space-y-2">
                      <Label htmlFor="subsidy-supplement-amount">
                        {i18n._({
                          id: "contracts.wizard.fields.subsidySupplementAmount",
                          message: "Subsidy supplement ({currency} / month)",
                          values: { currency },
                        })}
                      </Label>
                      <Input
                        id="subsidy-supplement-amount"
                        type="number"
                        step="0.05"
                        min="0"
                        max="10000"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={
                          wizardData.subsidySupplementAmount !== undefined
                            ? rappenToCHF(wizardData.subsidySupplementAmount)
                            : ""
                        }
                        onChange={(e) => {
                          if (e.target.value === "") {
                            onUpdateData({ subsidySupplementAmount: undefined });
                          } else {
                            const parsedCHF = parseFloat(e.target.value);
                            if (!isNaN(parsedCHF) && parsedCHF >= 0) {
                              onUpdateData({
                                subsidySupplementAmount: chfToRappen(parsedCHF),
                              });
                            }
                          }
                        }}
                        aria-label={i18n._(
                          msg({
                            id: "contracts.wizard.fields.subsidySupplementAmount.aria",
                            message: "Subventionszuschuss amount",
                          })
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        <Trans id="contracts.wizard.fields.subsidySupplementAmount.help">
                          Flat monthly grant billed to the subsidy office on top — never reduces the parent invoice.
                        </Trans>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    <Trans id="contracts.wizard.fields.notes">Notes</Trans>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="notes"
                    value={wizardData.notes || ""}
                    onChange={(e) => {
                      onUpdateData({ notes: e.target.value });
                    }}
                    placeholder={i18n._(
                      msg`Add any additional notes about this contract`
                    )}
                    className="resize-none min-h-[100px]"
                    aria-label={i18n._(msg`Contract notes`)}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "schedule":
        return (
          <div role="tabpanel" aria-labelledby="step-schedule">
            <ContractWizardStepSchedule
              servicePattern={wizardData.servicePattern || {}}
              setServicePattern={(pattern) =>
                onUpdateData({ servicePattern: pattern })
              }
              tariffVersions={wizardData.tariffVersions || {}}
              setTariffVersions={(versions) =>
                onUpdateData({ tariffVersions: versions })
              }
              childId={wizardData.childId || ""}
              services={services}
              subsidised={wizardData.subsidised}
              startDate={wizardData.startDate}
            />
          </div>
        );

      case "discounts":
        return (
          <div role="tabpanel" aria-labelledby="step-discounts">
            <ContractWizardStepDiscounts
              discounts={wizardData.discounts || {}}
              onDiscountsChange={(discounts) => onUpdateData({ discounts })}
              servicePattern={wizardData.servicePattern}
              tariffVersions={wizardData.tariffVersions}
              childId={wizardData.childId}
              services={services}
              subsidised={wizardData.subsidised}
              subsidyAmount={wizardData.subsidyAmount}
              siblingDiscountManual={wizardData.siblingDiscountManual}
              onSiblingDiscountManualChange={(siblingDiscountManual) =>
                onUpdateData({ siblingDiscountManual })
              }
              startDate={wizardData.startDate}
            />
          </div>
        );

      case "payers": {
        return (
          <div role="tabpanel" aria-labelledby="step-payers">
            <ContractWizardStepPayers
              payers={wizardData.payers || []}
              setPayers={(payers) => onUpdateData({ payers })}
              contactsData={contacts}
              childId={wizardData.childId}
              _childFirstname={selectedChild?.firstname}
              childLastname={selectedChild?.lastname}
              familyGroup={wizardData.familyGroup}
              setFamilyGroup={(group) => onUpdateData({ familyGroup: group })}
              autoSelectSharedParentAccount
            />
          </div>
        );
      }

      case "review": {
        return (
          <div
            role="tabpanel"
            aria-labelledby="step-review"
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-medium">
                <Trans id="contracts.wizard.review.headerTitle">
                  Review Contract
                </Trans>
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                <Trans id="contracts.wizard.review.description">
                  Review all contract details before creating the contract
                </Trans>
              </p>
            </div>

            {validReviewResult && validReviewResult.totalCostBreakdown ? (
              <EnhancedContractSummary
                key={`${wizardData.startDate || ""}-${
                  wizardData.endDate || ""
                }-${reviewCalculationDate || ""}`}
                costResult={validReviewResult}
                discounts={wizardData.discounts}
                subsidised={wizardData.subsidised}
                subsidyAmount={wizardData.subsidyAmount}
                services={services}
                groups={groups}
              />
            ) : (
              <div className="text-muted-foreground">
                <Trans id="contracts.wizard.review.noData">
                  Complete the previous steps to see the contract summary
                </Trans>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className="flex-1 px-6 py-4 overflow-y-auto"
      aria-live="polite"
      aria-atomic="true"
    >
      {renderStepContent()}
    </div>
  );
}
